import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { AttributeDictionaryType } from './types';

export const productAttributesEndpoint = 'api/v1/cms/master-product-attributes';

interface ResponseData {
  data: AttributeDictionaryType[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, { catalogCategoryId?: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export interface AttributeDictionaryParams {
  catalogCategoryId?: number;
}

/**
 * GET api/v1/cms/master-product-attributes/dictionary
 * Read-only shape used across the app to render/label EAV attribute values.
 * Optionally scoped to a catalog category (returns only the types applicable to it).
 */
export function useGetAttributeDictionaryApi(params: AttributeDictionaryParams = {}, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { catalogCategoryId } = params;
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  // Dictionary is an enhancement query; editors degrade to legacy options, so failures stay silent.
  const queryFn = async () => {
    const { data } = await http.get(`${productAttributesEndpoint}/dictionary`, {
      params: { catalogCategoryId },
    });

    return data;
  };

  return useQueryWrapper([`${productAttributesEndpoint}/dictionary`, { catalogCategoryId }], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn,
    staleTime: 300_000,
    retry: false,
    ...restOptions,
  });
}
